import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/v2/lib/utils";

interface CarouselSectionProps {
  title: string;
  children: ReactNode;
  /**
   * Vertical position classes for the scroll arrows. Defaults to centering
   * on the full row height, which is correct when a card's caption text
   * lives inside the card's own box (Spotlights, Upcoming Games, Recently
   * Completed). Highlights' caption sits outside/below the thumbnail
   * instead, so it overrides this to center on the thumbnail band alone.
   */
  arrowPositionClassName?: string;
  /** Where "View All" navigates to. Omit to leave it static (no destination yet). */
  viewAllHref?: string;
  /**
   * Whether the row offers "View All" at all. The Universe rows are windows
   * onto larger collections, so they always do. A row that is already the
   * whole set, such as a game's key moments, has nowhere to send anyone.
   */
  showViewAll?: boolean;
}

/**
 * Shared "title + View All + horizontal scroll row + arrows" shell used by
 * every Universe row.
 *
 * Each arrow only renders when there is actually something to scroll to in
 * that direction, so a row that fits (or one showing an empty/error
 * message) has no arrows at all, and neither end of a scrolled row offers a
 * control that would do nothing.
 */
export function CarouselSection({
  title,
  children,
  arrowPositionClassName = "top-1/2 -translate-y-1/2",
  viewAllHref,
  showViewAll = true,
}: CarouselSectionProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const syncArrows = useCallback(() => {
    const element = scrollRef.current;
    if (!element) return;
    const maxScroll = element.scrollWidth - element.clientWidth;
    // 1px tolerance: fractional layout widths mean scrollLeft rarely lands
    // exactly on 0 or on maxScroll, which would leave a dead arrow showing.
    setCanScrollLeft(element.scrollLeft > 1);
    setCanScrollRight(element.scrollLeft < maxScroll - 1);
  }, []);

  useEffect(() => {
    const element = scrollRef.current;
    if (!element) return;

    syncArrows();
    element.addEventListener("scroll", syncArrows, { passive: true });
    window.addEventListener("resize", syncArrows);

    // Catches the row being resized by something other than the window (a
    // sidebar collapsing). Guarded because jsdom has no ResizeObserver; the
    // window listener above still covers the common case without it.
    // Content changes (skeletons -> cards) re-run this effect via `children`.
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(syncArrows);
    observer?.observe(element);

    return () => {
      element.removeEventListener("scroll", syncArrows);
      window.removeEventListener("resize", syncArrows);
      observer?.disconnect();
    };
  }, [syncArrows, children]);

  const scrollBy = (direction: 1 | -1) => {
    const element = scrollRef.current;
    if (!element) return;
    element.scrollBy({
      left: direction * Math.max(240, element.clientWidth * 0.8),
      behavior: "smooth",
    });
  };

  const viewAllClassName =
    "font-semibold text-action text-sm inline-flex items-center gap-1";

  const arrowClassName =
    "absolute flex items-center justify-center w-9 h-9 rounded-full bg-white border border-slate-200 shadow-md text-slate-600 hover:text-action";

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <h2
          className="font-bold text-slate-900"
          // A global v1 rule leaves headings on a 50px line box, which puts an
          // empty band under every row title. Stated here so the title occupies
          // the space its own text needs.
          style={{ fontSize: "18px", lineHeight: 1.3 }}
        >
          {title}
        </h2>
        {!showViewAll ? null : viewAllHref ? (
          <Link to={viewAllHref} className={viewAllClassName}>
            View All
            <ChevronRight className="w-3.5 h-3.5" />
          </Link>
        ) : (
          <button
            type="button"
            disabled
            aria-disabled="true"
            className={cn(viewAllClassName, "text-slate-400 cursor-not-allowed")}
          >
            View All
            <ChevronRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      <div className="relative">
        <div
          ref={scrollRef}
          className="flex gap-4 overflow-x-auto scroll-smooth scrollbar-none"
        >
          {children}
        </div>

        {canScrollLeft && (
          <button
            type="button"
            onClick={() => scrollBy(-1)}
            aria-label={`Show previous ${title.toLowerCase()}`}
            className={cn(
              arrowClassName,
              "left-0 -translate-x-1/2",
              arrowPositionClassName,
            )}
          >
            <ChevronLeft className="w-4 h-4" strokeWidth={2.5} />
          </button>
        )}

        {canScrollRight && (
          <button
            type="button"
            onClick={() => scrollBy(1)}
            aria-label={`Show more ${title.toLowerCase()}`}
            className={cn(
              arrowClassName,
              "right-0 translate-x-1/2",
              arrowPositionClassName,
            )}
          >
            <ChevronRight className="w-4 h-4" strokeWidth={2.5} />
          </button>
        )}
      </div>
    </div>
  );
}
